import { useCallback, useEffect, useRef, useState } from "react";

const FAILURE_THRESHOLD = 3;
const FAILURE_WINDOW_MS = 2 * 60_000;
const OPEN_DURATION_MS = 60_000;

type CircuitMode = "closed" | "open" | "half-open";

export type OperationCircuitController = {
  isBlocking: boolean;
  canAttempt: boolean;
  recordFailure: () => void;
  recordSuccess: () => void;
};

/**
 * Circuit breaker for customer-triggered checkpoint and turnstile operations.
 * "Closed" is the normal state. Three infrastructure failures within two
 * minutes "open" the circuit and block new attempts for 60 seconds. It then
 * becomes "half-open" so the next action can test the service: success closes
 * the circuit, while another failure opens it again. This hook never retries an
 * operation; callers decide which outcomes to record as failures or successes.
 */
export function useOperationCircuit(
  onBlockingChange?: (blocked: boolean) => void,
): OperationCircuitController {
  const [mode, setMode] = useState<CircuitMode>("closed");
  const failures = useRef<number[]>([]);
  const timer = useRef<number>();

  const open = useCallback(() => {
    window.clearTimeout(timer.current);
    setMode("open");
    timer.current = window.setTimeout(() => setMode("half-open"), OPEN_DURATION_MS);
  }, []);

  const recordFailure = useCallback(() => {
    if (mode === "half-open") {
      open();
      return;
    }
    const now = Date.now();
    failures.current = failures.current
      .filter((timestamp) => now - timestamp < FAILURE_WINDOW_MS)
      .concat(now);
    if (failures.current.length >= FAILURE_THRESHOLD) open();
  }, [mode, open]);

  const recordSuccess = useCallback(() => {
    failures.current = [];
    setMode("closed");
  }, []);

  useEffect(() => {
    onBlockingChange?.(mode === "open");
  }, [mode, onBlockingChange]);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      onBlockingChange?.(false);
    },
    [onBlockingChange],
  );

  return {
    isBlocking: mode === "open",
    canAttempt: mode !== "open",
    recordFailure,
    recordSuccess,
  };
}

export function isInfrastructureFailure(status?: number) {
  return (
    status === undefined ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
}
